import { getSectionAskActionLabel, getSectionEditActionLabel } from '../utils/carousel-slide-focus'
import { ArrowLeft, MessageCircleMore, PenLine, SquarePen } from 'lucide-react'
import type { ReactNode } from 'react'
import { useComposerReadOnly } from '../components/composer-read-only'
import { useVariantMode } from '../components/variant-mode'

const MEDIA_FIELD_IDS = new Set(['image', 'video'])

interface SectionHoverWrapperProps {
  /** Field id passed to `requestFocus` (e.g. `image`, `caption`, `carousel_slide_2`). */
  fieldId: string
  onAskAI: () => void
  onEdit: () => void
  isActiveEditing?: boolean
  children: ReactNode
}

const btnClass =
  'flex cursor-pointer items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-800 shadow-lg transition-all duration-150 scale-95 group-hover/section:scale-100 hover:scale-105 hover:bg-white'

/**
 * Desktop hover overlay for social preview sections (Instagram, LinkedIn, Facebook).
 * Shows scoped Ask AI / Change-or-Edit actions on hover.
 */
export function SectionHoverWrapper({
  fieldId,
  onAskAI,
  onEdit,
  isActiveEditing,
  children,
}: SectionHoverWrapperProps) {
  const askLabel = getSectionAskActionLabel(fieldId)
  const editLabel = getSectionEditActionLabel(fieldId)
  const readOnly = useComposerReadOnly()
  const variant = useVariantMode()

  // Live post → view-only: render the section without the Ask AI / Edit hover
  // affordances so there's no edit entry point.
  if (readOnly) return <>{children}</>

  // One Post model, platform tab: the section itself explains the contract.
  if (variant) {
    const isMedia = MEDIA_FIELD_IDS.has(fieldId) || fieldId.startsWith('carousel_slide')
    if (isMedia) {
      // Shared visual: the only action is going to the post's editor.
      return (
        <div className="group/section relative" data-field={fieldId}>
          {children}
          <div className="pointer-events-none absolute inset-0 z-10 hidden flex-col items-center justify-center gap-2 bg-black/50 opacity-0 transition-all duration-200 group-hover/section:pointer-events-auto group-hover/section:opacity-100 md:flex">
            <span className="text-[12px] font-medium text-white/90">
              Visuals are shared across platforms
            </span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                variant.onGoToPost()
              }}
              className={btnClass}
            >
              <ArrowLeft size={14} />
              Edit on the post
            </button>
          </div>
        </div>
      )
    }
    if (!variant.customized) {
      // Auto-adapted copy: one door, the explicit customize gate. The inline
      // editors underneath stay inert until the gate unlocks them.
      return (
        <div className="group/section relative" data-field={fieldId}>
          <div className="pointer-events-none">{children}</div>
          <div className="absolute inset-0 z-10 hidden cursor-pointer flex-col items-center justify-center gap-2 rounded-[6px] bg-black/50 opacity-0 transition-all duration-200 group-hover/section:opacity-100 md:flex">
            <span className="text-[12px] font-medium text-white/90">
              Auto-adapted from your post
            </span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                variant.onCustomizeRequest()
              }}
              className={btnClass}
            >
              <PenLine size={14} />
              Customize for {variant.platformLabel}
            </button>
          </div>
        </div>
      )
    }
    // Customized copy: plain inline editing — the user owns these words.
    return (
      <div className="relative" data-field={fieldId}>
        {children}
      </div>
    )
  }

  return (
    <div className="group/section relative" data-field={fieldId}>
      {children}
      {isActiveEditing && (
        <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
      )}
      <div className="pointer-events-none absolute inset-0 z-10 hidden items-center justify-center gap-3 bg-black/40 opacity-0 ring-1 ring-white/20 transition-all duration-200 ring-inset group-hover/section:pointer-events-auto group-hover/section:opacity-100 md:flex">
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onAskAI()
          }}
          className={btnClass}
          aria-label={askLabel}
        >
          <MessageCircleMore size={14} />
          {askLabel}
        </button>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onEdit()
          }}
          className={btnClass}
          aria-label={editLabel}
        >
          <SquarePen size={14} />
          {editLabel}
        </button>
      </div>
    </div>
  )
}
